import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Wallet, ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { inr, fmtDate } from "@/utils";
import { toast } from "sonner";

export default function VendorLedger() {
  const qc = useQueryClient();
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const [vendorId, setVendorId] = useState("");
  const { data: ledger } = useQuery({
    queryKey: ["vendor-ledger", vendorId],
    queryFn: async () => (await api.get(`/vendors/${vendorId}/ledger`)).data,
    enabled: !!vendorId,
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ amount: 0, date: new Date().toISOString().slice(0, 10), payment_mode: "bank_transfer", reference_number: "", notes: "" });

  function openEdit(entry) {
    setEditing(entry.entry_id);
    setForm({
      amount: entry.credit || 0,
      date: entry.date,
      payment_mode: entry.payment_mode || "bank_transfer",
      reference_number: entry.reference_number || "",
      notes: entry.notes || "",
    });
    setOpen(true);
  }

  function openNew() {
    setEditing(null);
    setForm({ amount: 0, date: new Date().toISOString().slice(0, 10), payment_mode: "bank_transfer", reference_number: "", notes: "" });
    setOpen(true);
  }

  async function savePayment() {
    if (!vendorId) return toast.error("Pick a vendor");
    if (!form.amount) return toast.error("Amount required");
    const vendor = vendors.find((v) => v.id === vendorId);
    try {
      if (editing) {
        await api.patch(`/vendor-payments/${editing}`, { ...form, amount: +form.amount });
        toast.success("Payment updated");
      } else {
        await api.post("/vendor-payments", {
          ...form, amount: +form.amount, vendor_id: vendorId, vendor_name: vendor?.name || "",
        });
        toast.success("Payment recorded");
      }
      qc.invalidateQueries({ queryKey: ["vendor-ledger", vendorId] });
      setOpen(false);
      setEditing(null);
      setForm({ amount: 0, date: new Date().toISOString().slice(0, 10), payment_mode: "bank_transfer", reference_number: "", notes: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function deletePayment(entry) {
    if (!window.confirm(`Delete payment of ${inr(entry.credit)} on ${fmtDate(entry.date)}?`)) return;
    try {
      await api.delete(`/vendor-payments/${entry.entry_id}`);
      toast.success("Payment deleted");
      qc.invalidateQueries({ queryKey: ["vendor-ledger", vendorId] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="vendor-ledger-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Procurement</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Vendor Ledger</h1>
          <p className="text-sm text-zinc-500 mt-1">Full statement per vendor — opening, purchases, payments and running outstanding.</p>
        </div>
        <div className="flex gap-2">
          <Select value={vendorId} onValueChange={setVendorId}>
            <SelectTrigger className="w-72" data-testid="vl-vendor"><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
            <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} · {v.category}</SelectItem>)}</SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" disabled={!vendorId} data-testid="record-payment-btn" onClick={openNew}><Plus size={14} className="mr-1" /> Record Payment</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Record"} Vendor Payment</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="vp-amount" /></div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>Payment Mode</Label>
                  <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                    <SelectTrigger data-testid="vp-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="rtgs">RTGS/NEFT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Reference #</Label><Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} /></div>
                <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={savePayment} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-payment">{editing ? "Save changes" : "Record"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {!vendorId ? (
        <Card className="bg-white border-dashed border-zinc-300"><CardContent className="p-16 text-center text-sm text-zinc-500 flex flex-col items-center gap-2">
          <ArrowLeft size={20} className="rotate-45 opacity-40" />
          Pick a vendor above to see their complete statement.
        </CardContent></Card>
      ) : !ledger ? (
        <div className="text-sm text-zinc-500">Loading ledger…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Opening Balance</div><div className="kpi-num mt-1">{inr(ledger.opening_balance)}</div></CardContent></Card>
            <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Purchases</div><div className="kpi-num mt-1 text-red-600">{inr(ledger.total_debit)}</div></CardContent></Card>
            <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Payments</div><div className="kpi-num mt-1 text-emerald-700">{inr(ledger.total_credit)}</div></CardContent></Card>
            <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Outstanding</div><div className={`kpi-num mt-1 ${ledger.outstanding > 0 ? "text-red-600" : "text-emerald-700"}`}>{inr(ledger.outstanding)}</div></CardContent></Card>
          </div>

          <Card className="bg-white border-zinc-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-zinc-50"><TableRow>
                  <TableHead>Date</TableHead><TableHead>Type</TableHead>
                  <TableHead>Reference</TableHead><TableHead>Description</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right w-[110px]">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {ledger.entries.length > 0 && (
                    <TableRow className="bg-zinc-50/60">
                      <TableCell colSpan={6} className="text-xs italic text-zinc-500">Opening balance</TableCell>
                      <TableCell className="text-right font-mono">{inr(ledger.opening_balance)}</TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                  {ledger.entries.map((e, i) => (
                    <TableRow key={e.entry_id || e.id || `${e.date}-${e.type}-${i}`} className="data-row" data-testid={`ledger-row-${i}`}>
                      <TableCell className="text-xs font-mono">{fmtDate(e.date)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${e.type === "purchase" ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                          <Wallet size={11} /> {e.type}
                          {e.status && e.status !== "approved" && <span className="ml-1 uppercase">· {e.status}</span>}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs font-mono">{e.ref}</TableCell>
                      <TableCell className="text-xs">{e.description}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{e.debit ? inr(e.debit) : "—"}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{e.credit ? inr(e.credit) : "—"}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{inr(e.balance)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {e.type === "payment" && e.entry_id ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="mr-1 h-7 px-2"
                              onClick={() => openEdit(e)}
                              data-testid={`edit-payment-${e.entry_id}`}
                              title="Edit payment"
                            >
                              <Pencil size={12} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => deletePayment(e)}
                              data-testid={`delete-payment-${e.entry_id}`}
                              title="Delete payment"
                            >
                              <Trash2 size={12} />
                            </Button>
                          </>
                        ) : (
                          <span className="text-[10px] text-zinc-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!ledger.entries.length && <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-10">No transactions with this vendor yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
